import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

function FundSizeFilter({ MinFundOptions, setMinFundOptions, MaxFundOptions, setMaxFundOptions, customPathname, label = "Fund Size (US$ M)", isPopupOpen, setIsPopupOpen }) {

    const { MinFundSize, MaxFundSize, refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
    const [error, setError] = useState("");

    /* The `useEffect` hook in this code is used to update the `MinFundOptions` and `MaxFundOptions`
    based on the selected filters. */
    useEffect(() => {
        const refineOptions = refineSearchFilters[customPathname]?.fundSize?.at(0);
        const maxRefineOptions = refineSearchFilters[customPathname]?.fundSize?.at(1);
        setMinFundOptions(MinFundSize?.map(option => ({ ...option, isChecked: refineOptions?.name == option.name })));
        setMaxFundOptions(MaxFundSize?.map(option => ({ ...option, isChecked: maxRefineOptions?.name == option.name })));
    }, [refineSearchFilters, customPathname, setMinFundOptions, MinFundSize, setMaxFundOptions, MaxFundSize]);

    const selectedMin = MinFundOptions?.filter(option => option.isChecked)?.at(0);
    const selectedMax = MaxFundOptions?.filter(option => option.isChecked)?.at(0);

    /**
     * The function `handleMinRange` updates the minimum fund options based on the selected minimum
     * size and displays a warning if the selected minimum size is greater than the selected maximum
     * size.
     */
    const handleMinRange = (e) => {
        const { value } = e.target;
        
        // Clear error when user changes value
        setError("");
        
        if (selectedMax?.name !== "" && selectedMax?.name != null && (parseInt(value) > parseInt(selectedMax?.name))) {
            setError('From value must be less than To value');
            return;
        }
        setMinFundOptions(MinFundOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.name === value })));
    };

    /**
     * The function `handleMaxRange` updates the `MaxFundOptions` array based on the selected maximum
     * size value, and displays a warning if the selected maximum size is less than the selected
     * minimum size.
     */
    const handleMaxRange = (e) => {
        const { value } = e.target;
        
        // Clear error when user changes value
        setError("");
        
        if (selectedMin?.name !== "" && selectedMin?.name != null && (parseInt(selectedMin?.name) > parseInt(value))) {
            setError('To value must be greater than From value');
            return;
        }
        setMaxFundOptions(MaxFundOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.name === value })));
    };

    const getDisplayText = () => {
        if (selectedMin?.name && selectedMax?.name) {
            return `: ${selectedMin.name} to ${selectedMax.name}`;
        } else if (selectedMin?.name) {
            return `: From ${selectedMin.name}`;
        } else if (selectedMax?.name) {
            return `: Up to ${selectedMax.name}`;
        }
        return " ";
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {(selectedMin?.name || selectedMax?.name) && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    {/* <div className="p-4 min-w-[300px]"> */}
                    <div className="p-4 min-w-[225px] md:min-w-[300px]">
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                From (US$ M)
                            </label>
                            <select 
                                value={selectedMin?.name ?? ""} 
                                onChange={handleMinRange}
                                className={`w-full p-2 border rounded focus:outline-none bg-white ${
                                    error && parseInt(selectedMin?.name) > parseInt(selectedMax?.name)
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                        : 'border-[#BC8B20]'
                                }`}
                            >
                                <option value="">All</option>
                                {MinFundOptions && MinFundOptions.length > 0 ? (
                                    MinFundOptions.map((option, index) => (
                                        <option key={index} value={option.name ? option.name : ''}>
                                            {option.name ? option.name : ''}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No options available</option>
                                )}
                            </select>
                        </div>
                        
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                To (US$ M)
                            </label>
                            <select 
                                value={selectedMax?.name ?? ""} 
                                onChange={handleMaxRange}
                                className={`w-full p-2 border rounded focus:outline-none bg-white ${
                                    error && parseInt(selectedMin?.name) > parseInt(selectedMax?.name)
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                        : 'border-[#BC8B20]'
                                }`}
                            >
                                <option value="">All</option>
                                {MaxFundOptions && MaxFundOptions.length > 0 ? (
                                    MaxFundOptions.map((option, index) => (
                                        <option key={index} value={option.name ? option.name : ''}>
                                            {option.name ? option.name : ''}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No options available</option>
                                )}
                            </select>
                        </div>
                        
                        {error && (
                            <div className="text-red-600 text-xs font-medium">
                                {error}
                            </div>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default FundSizeFilter;